import React from 'react';
import styled from '@emotion/styled';
import { radius, spacing, status, typography } from '../../style-utils/theme';

/**
 * THE SHARED STATUS PILL — one label, one tint, read from the `status` triples.
 *
 * design_system.md §9 promised a shared pill and none existed: eight screens
 * each grew their own (the units Pill with a dot, the exit desk's plain one, the
 * roster Tag…). Those stay as they are; new surfaces use this one.
 *
 * TEXT IS ALWAYS PRESENT. The tone backs the word up, it never replaces it — a
 * pill that is only a colour tells a colour-blind reader nothing. `children`
 * is therefore required, and the dot is decorative (aria-hidden).
 *
 * Named after the SIGNAL, not the colour, so a palette change never leaves a
 * caller asking for "the green one".
 */

export type StatusTone = keyof typeof status;

type ToneProps = { $tone: StatusTone };

// Block-body prop readers, kept out of the template so each dynamic value is a
// bare interpolation (the same shape as the units Pill).
const toneBg = (p: ToneProps) => {
  return status[p.$tone].bg;
};
const toneFg = (p: ToneProps) => {
  return status[p.$tone].fg;
};
const toneDot = (p: ToneProps) => {
  return status[p.$tone].dot;
};

const Pill = styled.span<ToneProps>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: ${spacing.half};
  max-width: 100%;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${toneBg};
  color: ${toneFg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  white-space: nowrap;
`;

const Dot = styled.span<ToneProps>`
  flex: none;
  width: ${spacing.xs};
  height: ${spacing.xs};
  border-radius: ${radius.circle};
  background: ${toneDot};
`;

export interface StatusPillProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone: StatusTone;
  /** A leading dot in the tone's own `dot` colour. Decorative. */
  dot?: boolean;
  children: React.ReactNode;
}

export const StatusPill: React.FC<StatusPillProps> = ({
  tone,
  dot = false,
  children,
  ...rest
}) => (
  <Pill $tone={tone} data-tone={tone} {...rest}>
    {dot && <Dot $tone={tone} aria-hidden="true" />}
    {children}
  </Pill>
);
